import { Box, CircularProgress } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { examCategories } from "../../Utils/Constants";
import styles from "./ExamCategoriesSelectionStep.module.css";

export interface ExamCategory {
  _id?: string;
  name: string;
  label?: string;
}

export interface ExamCategoriesSelectionStepProps {
  categories: ExamCategory[];
  selectedCategory: ExamCategory | null;
  onCategorySelect: (category: ExamCategory) => void;
  loading?: boolean;
}

/**
 * Helper function to check if a category is selected
 * Handles both _id-based and name-based comparison for categories without _id
 */
const isCategorySelected = (
  selected: ExamCategory | null,
  category: ExamCategory
): boolean => {
  if (!selected) return false;

  // If both have _id, compare by _id
  if (selected._id && category._id) {
    return selected._id === category._id;
  }

  // If neither has _id or one is missing, compare by name
  return selected.name === category.name;
};

export default function ExamCategoriesSelectionStep({
  categories,
  selectedCategory,
  onCategorySelect,
  loading = false,
}: ExamCategoriesSelectionStepProps) {
  const getCategoryData = (categoryName: string) => {
    const categoryKey = categoryName.toUpperCase();
    const categoryData = examCategories[
      categoryKey as keyof typeof examCategories
    ] as any;
    return categoryData;
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress size={40} sx={{ marginBottom: "1rem" }} />
        <p className={styles.emptyStateText}>{Vocabulary.loading}...</p>
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <svg
          className={styles.emptyStateIcon}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className={styles.emptyStateText}>
          {Vocabulary.noExamCategoriesAvailable}
        </p>
      </Box>
    );
  }

  const handleCategorySelect = (category: ExamCategory) => {
    onCategorySelect(category);
  };

  const isSelected = (category: ExamCategory) =>
    isCategorySelected(selectedCategory, category);

  return (
    <div className={styles.container}>
      {categories.map((category) => {
        const categoryData = getCategoryData(category.name);
        return (
          <div
            key={category._id || category.name}
            className={`${styles.card} ${
              isSelected(category) ? styles.cardSelected : ""
            }`}
            onClick={() => handleCategorySelect(category)}
          >
            <div className={styles.iconContainer}>
              <div className={styles.icon}>{categoryData?.icon}</div>
            </div>

            <div className={styles.header}>
              <div>
                <h3 className={styles.categoryName}>
                  {Vocabulary.category}: {category.name}
                </h3>
                <p className={styles.categoryLabel}>{categoryData?.label}</p>
              </div>
            </div>

            <button
              className={`${styles.selectButton} ${
                isSelected(category)
                  ? styles.selectButtonSelected
                  : styles.selectButtonDefault
              }`}
              onClick={(e) => {
                e.stopPropagation();
                handleCategorySelect(category);
              }}
            >
              {isSelected(category) ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {Vocabulary.selected}
                </>
              ) : (
                Vocabulary.select
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
